import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Image as ImageIcon, Loader2, Paperclip, X } from "lucide-react";
import { api, API } from "@/lib/api";
import { track } from "@/lib/site";

const MAX_MB = 15;

function Field({ label, children, wide }) {
    return (
        <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
            <span className="mono-label text-muted_text mb-2 block normal-case tracking-[0.04em]">{label}</span>
            {children}
        </label>
    );
}

function Uploader({ label, hint, button, uploadingLabel, accept, max, files, setFiles, icon: Icon, t, testid }) {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    const onPick = async (e) => {
        const picked = Array.from(e.target.files || []).slice(0, max - files.length);
        e.target.value = "";
        setBusy(true);
        for (const file of picked) {
            if (file.size > MAX_MB * 1024 * 1024) { toast.error(`${file.name} ${t.errors.big}`); continue; }
            const fd = new FormData();
            fd.append("file", file);
            try {
                const { data } = await axios.post(`${API}/uploads/attachment`, fd);
                setFiles((prev) => [...prev, data]);
            } catch (err) {
                toast.error(err?.response?.data?.detail || `${t.errors.upload} ${file.name}`);
            }
        }
        setBusy(false);
    };
    return (
        <div>
            <p className="mono-label text-muted_text mb-2 normal-case tracking-[0.04em]">{label}</p>
            <input ref={ref} type="file" multiple accept={accept} onChange={onPick} className="hidden" data-testid={`${testid}-input`} />
            <button type="button" onClick={() => ref.current?.click()} disabled={busy || files.length >= max} data-testid={`${testid}-button`}
                    className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-forest/40 bg-cream/60 px-5 py-5 text-sm text-forest hover:border-forest hover:bg-cream disabled:opacity-50 transition-colors">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
                {busy ? uploadingLabel : button}
            </button>
            <p className="mt-2 text-xs text-muted_text leading-relaxed">{hint}</p>
            {files.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid={`${testid}-list`}>
                    {files.map((f, i) => (
                        <li key={f.id} className="flex items-center justify-between gap-3 rounded-lg bg-cream px-4 py-2.5 text-sm">
                            <span className="truncate">{f.filename}</span>
                            <button type="button" aria-label={`Remove ${f.filename}`} onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="text-muted_text hover:text-forest"><X className="h-4 w-4" /></button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function LandForm({ t, lang }) {
    const f = t.fields;
    const [form, setForm] = useState({ seller_name: "", email: "", phone: "", location: "", municipality: "", land_area: "", asking_price: "", classification: "", description: "", consent: false, website: "" });
    const [docs, setDocs] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k) => (e) => setForm((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent) { toast.error(t.errors.consent); return; }
        setSending(true);
        try {
            await api.post("/land-submissions", { ...form, lang, documents: docs.map((d) => d.id), photos: photos.map((p) => p.id) });
            toast.dismiss();
            track("land_submission", lang);
            setDone(true);
            window.scrollTo({ top: document.getElementById("submit-land")?.offsetTop - 90, behavior: "smooth" });
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : t.errors.generic);
        } finally { setSending(false); }
    };

    if (done) {
        return (
            <div data-testid="land-success" className="rounded-2xl bg-forest text-cream p-10 md:p-14">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-sage text-forest mb-8"><Check className="h-6 w-6" /></span>
                <p className="font-serif text-3xl md:text-4xl leading-tight mb-4">{t.successTitle}</p>
                <p className="text-cream/80 leading-relaxed max-w-lg">{t.successBody}</p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} data-testid="land-form" className="scroll-mt-24 rounded-2xl bg-surface p-6 sm:p-8 md:p-10 space-y-6">
            <div className="grid sm:grid-cols-2 gap-5">
                <Field label={f.name}><input required value={form.seller_name} onChange={set("seller_name")} className="editorial-input" autoComplete="name" data-testid="land-name" /></Field>
                <Field label={f.email}><input required type="email" value={form.email} onChange={set("email")} className="editorial-input" autoComplete="email" data-testid="land-email" /></Field>
                <Field label={f.phone}><input required type="tel" value={form.phone} onChange={set("phone")} className="editorial-input" autoComplete="tel" data-testid="land-phone" /></Field>
                <Field label={f.municipality}><input required value={form.municipality} onChange={set("municipality")} placeholder={f.municipalityPh} className="editorial-input" data-testid="land-municipality" /></Field>
                <Field label={f.location} wide><input required value={form.location} onChange={set("location")} placeholder={f.locationPh} className="editorial-input" data-testid="land-location" /></Field>
                <Field label={f.area}><input required value={form.land_area} onChange={set("land_area")} placeholder={f.areaPh} className="editorial-input" data-testid="land-area" /></Field>
                <Field label={f.price}><input value={form.asking_price} onChange={set("asking_price")} placeholder={f.pricePh} className="editorial-input" data-testid="land-price" /></Field>
                <Field label={f.classification} wide><input value={form.classification} onChange={set("classification")} placeholder={f.classificationPh} className="editorial-input" data-testid="land-classification" /></Field>
                <Field label={f.description} wide><textarea rows={4} value={form.description} onChange={set("description")} placeholder={f.descriptionPh} className="editorial-input resize-y" data-testid="land-description" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
                <Uploader label={f.docs} hint={f.docsHint} button={f.add} uploadingLabel={f.uploading} icon={Paperclip} max={8} t={t} testid="land-docs"
                          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.zip,.doc,.docx,.xls,.xlsx,.kml,.kmz" files={docs} setFiles={setDocs} />
                <Uploader label={f.photos} hint={f.photosHint} button={f.addPhotos} uploadingLabel={f.uploading} icon={ImageIcon} max={12} t={t} testid="land-photos"
                          accept="image/jpeg,image/png,image/webp,image/heic,.heic" files={photos} setFiles={setPhotos} />
            </div>
            <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
            <label className="flex items-start gap-3 text-sm text-muted_text leading-relaxed cursor-pointer">
                <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 accent-forest" data-testid="land-consent" />
                <span>{t.consent} <Link to="/legal/privacy" className="underline hover:text-forest">{t.privacy}</Link>.</span>
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-5 pt-2">
                <button type="submit" disabled={sending} data-testid="land-submit"
                        className="group inline-flex items-center justify-center gap-3 rounded-full bg-forest text-cream hover:bg-forest-hover px-8 py-4 text-sm font-medium transition-colors disabled:opacity-60">
                    {sending && <Loader2 className="h-4 w-4 animate-spin" />}{t.submit}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
            </div>
            <p className="text-xs text-muted_text leading-relaxed" data-testid="land-disclaimer">{t.disclaimer}</p>
        </form>
    );
}
